import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight, MapPin, Plane, GraduationCap, Briefcase, Users, HelpCircle,
  FileCheck2, FolderOpen, GitCompareArrows, UserCheck, ShieldCheck, KeyRound,
  Link2, History, CheckCircle2, MessageCircle, CalendarClock, Lock, ScrollText,
  Check, X, ChevronRight, Compass, ClipboardCheck, Sparkles,
} from "lucide-react";
import Nav from "@/components/marketing/Nav";
import Footer from "@/components/marketing/Footer";
import { Reveal, RevealGroup, Item } from "@/components/Reveal";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import api from "@/lib/api";

const HERO_IMG = "https://customer-assets-gfyr7b9c.emergentagent.net/job_visa-workflow-6/artifacts/mawzi01o_image.png";
const SERVICE_IMG = "https://images.unsplash.com/photo-1758272422305-39a02e94273c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxwZXJzb24lMjBvcmdhbml6aW5nJTIwcGFzc3BvcnQlMjB0cmF2ZWwlMjBkb2N1bWVudHMlMjBhdCUyMGRlc2t8ZW58MHx8fHwxNzkwNjg2ODE1fDA&ixlib=rb-4.1.0&q=85";
const PLAN_IMG = "https://images.unsplash.com/photo-1655722723663-75b47de17a31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjBwbGFubmluZyUyMHRyaXAlMjB3b3JsZCUyMG1hcCUyMGxhcHRvcCUyMHBhc3Nwb3J0JTIwZGVza3xlbnwwfHx8fDE3OTA3MDgwMzN8MA&ixlib=rb-4.1.0&q=85";
const STUDENT_IMG = "https://images.unsplash.com/photo-1663162550938-60f70fab5d31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxzdHVkZW50cyUyMHdhbGtpbmclMjB1bml2ZXJzaXR5JTIwY2FtcHVzJTIwYWJyb2FkfGVufDB8fHx8MTc5MDcwODAzM3ww&ixlib=rb-4.1.0&q=85";
const FAMILY_IMG = "https://images.unsplash.com/photo-1772466669620-310c35f9deb2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxmYW1pbHklMjB3aXRoJTIwbHVnZ2FnZSUyMGF0JTIwYWlycG9ydCUyMHRyYXZlbHxlbnwwfHx8fDE3OTA3MDgwMzN8MA&ixlib=rb-4.1.0&q=85";

function Eyebrow({ children, light = false }) {
  return <p className={`mb-4 text-sm font-bold uppercase tracking-[0.2em] sm:text-base ${light ? "text-brandyellow" : "text-coral"}`}>{children}</p>;
}

function Section({ id, children, className = "" }) {
  return <section id={id} className={`mx-auto max-w-page px-5 py-16 sm:px-8 sm:py-20 md:py-24 lg:px-12 ${className}`}>{children}</section>;
}

const YellowBtn = ({ to, children, testid }) => (
  <Link to={to} data-testid={testid}
    className="inline-flex items-center justify-center gap-2 rounded bg-brandyellow px-7 py-3.5 text-sm font-semibold text-heading transition-all duration-300   active:translate-y-0">
    {children} <ArrowRight size={17} />
  </Link>
);

const GhostBtn = ({ to, children, testid, icon: Icon = MessageCircle }) => (
  <Link to={to} data-testid={testid}
    className="inline-flex items-center justify-center gap-2 rounded border border-hborder bg-white px-7 py-3.5 text-sm font-semibold text-navy transition-colors hover:border-navy">
    <Icon size={16} /> {children}
  </Link>
);

// ---- static content ----
const purposes = [
  { Icon: Plane, title: "Tourism", body: "Prepare for a holiday, a sightseeing trip or a short visit abroad." },
  { Icon: GraduationCap, title: "Study", body: "Get your application ready for studying at a school or university abroad." },
  { Icon: Briefcase, title: "Business", body: "Prepare for permitted business travel and the documents that go with it." },
  { Icon: Users, title: "Family visit", body: "Prepare to visit family or relatives living in another country." },
  { Icon: HelpCircle, title: "Something else", body: "Tell our team about your trip and we will help you find the right service." },
];

const journey = [
  ["01", "Tell us about your trip", "Choose your destination, travel purpose and the visa service that fits."],
  ["02", "Upload your passport", "We use your passport details to begin preparing your case straight away."],
  ["03", "Answer a few questions", "We collect only the information your application actually needs."],
  ["04", "Add your documents", "Upload your supporting evidence and keep everything together in one place."],
  ["05", "We prepare your application", "Our technology organizes your information and speeds up the repetitive work."],
  ["06", "A real person checks it", "A Holavisto specialist reviews your preparation and flags anything that needs attention."],
  ["07", "You review everything", "See the prepared information and request corrections before you continue."],
  ["08", "We book your visa appointment", "Where the process allows, we book your official visa appointment for you, then guide you through biometrics and submission."],
];

const handles = [
  { Icon: FileCheck2, title: "Application preparation", body: "We use the details you give us to prepare your application and organize everything your chosen service needs." },
  { Icon: FolderOpen, title: "Document organization", body: "Upload your supporting documents in one place. We arrange them around the requirements for your case." },
  { Icon: GitCompareArrows, title: "Application checks", body: "We compare the important details across your documents and application and highlight anything to fix." },
  { Icon: UserCheck, title: "Human review", body: "A real member of our team reviews your preparation before you continue to the official process." },
  { Icon: CalendarClock, title: "Visa appointment booking", body: "Once your application is ready, we book your official visa appointment for you and keep you updated. One of the hardest parts of the journey, handled." },
];

const problems = [
  ["Too many sources", "Requirements can be spread across government pages, application systems and visa-centre instructions."],
  ["Too many documents", "Passports, finances, employment, education and travel plans all need to be gathered and organized."],
  ["Small details matter", "Names, dates and addresses can quietly disagree from one document to the next."],
  ["Unclear next steps", "Once one task is done, it is often hard to know what needs to happen next."],
];

const withUs = ["Your visa service is identified", "Your requirements are organized", "Your documents stay together", "Your application is prepared", "Important details are checked", "A human reviews your preparation", "You get clear next steps"];
const onYourOwn = ["You work out the process", "You research the requirements", "You organize everything yourself", "You complete the application", "You check every detail by hand", "You handle the review yourself", "You track the next steps alone"];

const packages = [
  { name: "Guided preparation", tag: "For self-starters", price: 60, body: "For travellers who want to prepare their own application with a clear, guided process.", feats: ["Personalized requirements", "Document checklist", "Secure document upload", "Document organization", "Application guidance", "Case tracking"], cta: "Choose guided", to: "/register", highlight: false },
  { name: "Assisted preparation", tag: "Full preparation", price: 80, body: "For travellers who want Holavisto to handle the preparation work for them.", feats: ["Everything in Guided", "Application preparation", "Information extraction", "Consistency checks", "Human review", "Appointment guidance", "Customer support"], cta: "Choose assisted", to: "/register", highlight: true },
  { name: "Expert review", tag: "Second opinion", price: 70, body: "For travellers who have already prepared and want a professional review before the official process.", feats: ["Document review", "Application review", "Consistency review", "Missing-information review", "Expert feedback", "Final preparation review"], cta: "Choose expert review", to: "/register", highlight: false },
  { name: "Professional support", tag: "Represented cases", price: 80, body: "For cases where appropriately qualified professional immigration support is required or preferred.", feats: ["Case coordination", "Professional review", "Specialist support", "Representation where applicable"], cta: "Talk to a specialist", to: "/consultation", highlight: false },
];

// Holavisto service fee per destination (EUR). Government & provider fees are separate.
const corridorPrices = { germany: 70, uk: 80, schengen: 70, usa: 80, canada: 60, australia: 60 };

const feeLines = [
  ["Holavisto service fee", "What you pay us for preparing and supporting your application."],
  ["Government visa fee", "Set by the relevant authority and shown separately where it applies."],
  ["Visa centre or provider fee", "Charged by an official provider where the process requires it."],
  ["Other third-party costs", "Any additional official costs, shown clearly before you pay."],
];

const reviewRows = [
  ["Passport information", "Checked", "ok"],
  ["Travel information", "Checked", "ok"],
  ["Supporting documents", "Checked", "ok"],
  ["Application information", "Reviewed", "ok"],
  ["Missing information", "Needs attention", "warn"],
  ["Correction request", "Available", "info"],
];

const appointmentHelp = ["Booking your visa appointment", "Finding the earliest available slots", "Official provider and centre details", "Preparing you for the appointment", "Appointment reminders", "Rescheduling support where allowed"];
const appointmentYou = ["Complete identity verification", "Provide your biometrics", "Attend an interview if required", "Appear in person when required", "Complete applicant-only official steps"];

const vaultCards = ["Passport details", "Applicant information", "Previous applications", "Travel documents", "Education records", "Employment documents"];

const applicantFeats = ["Visa service selection", "Personalized requirements", "Application preparation", "Document organization", "Consistency checks", "Human review", "Appointment guidance", "Secure case history"];
const agencyFeats = ["Client management", "Case pipeline", "Document collection", "Case assignments", "Review queue", "Applicant communication", "Deadline tracking", "Audit history"];

const trust = [
  { Icon: Lock, title: "Private document handling", body: "Your documents are stored privately and accessed only through authorized workflows." },
  { Icon: KeyRound, title: "Permission control", body: "Access to cases and documents is limited by each user's role and permissions." },
  { Icon: Link2, title: "Source-linked requirements", body: "Requirements are connected to their underlying sources and verification details." },
  { Icon: ShieldCheck, title: "Originals stay unchanged", body: "We never silently alter the original documents you upload." },
  { Icon: History, title: "Audit history", body: "Important actions are recorded so there is a clear history of your case." },
  { Icon: UserCheck, title: "Human support", body: "When you need help, a member of the team can review your preparation." },
];

const why = [
  { Icon: ClipboardCheck, title: "Clear requirements", body: "Know exactly what your chosen visa service needs." },
  { Icon: FolderOpen, title: "Organized documents", body: "Keep all your application evidence in one place." },
  { Icon: Sparkles, title: "Smart preparation", body: "Let technology speed up the repetitive preparation work." },
  { Icon: GitCompareArrows, title: "Consistency checks", body: "Catch important differences across your information early." },
  { Icon: UserCheck, title: "Human review", body: "Have a real person review your preparation." },
  { Icon: Compass, title: "Clear next steps", body: "Always know what you need to do next." },
];

const faqs = [
  ["Can Holavisto prepare my visa application for me?", "Yes. Depending on the service you choose, we can help prepare the application, organize your documents, check the important information and provide human review before you continue through the official process."],
  ["What do I need to provide?", "You normally start with your passport and a few details about your trip. From there we guide you through the documents and questions that are relevant to your visa service."],
  ["Will Holavisto complete the application for me?", "Where the service allows it, our team can prepare the application from the information and documents you provide. You always review the information before the official submission step."],
  ["Do I still need to attend a visa centre or embassy?", "Some destinations require you to verify your identity, provide biometrics, attend an interview or appear in person. We explain exactly what your service requires."],
  ["Can Holavisto book my appointment?", "Appointment assistance depends on the destination and official process. Where permitted, we can help with booking. Where applicant action is required, we guide you through the official steps."],
  ["Do I pay the government visa fee to Holavisto?", "Government fees are separate from the Holavisto service fee where they apply. Before any payment, we clearly show which charges relate to what."],
  ["Can I review my application before it is submitted?", "Yes. You stay in control of the information in your application. Where we prepare it for you, you can review it and request corrections before continuing."],
  ["What happens if a document is missing?", "We identify the missing information or document and explain what needs attention so you can provide the right evidence."],
  ["Can my family apply together?", "Yes, where the relevant process allows group or family applications. We organize the shared trip information while keeping each traveller's personal details separate."],
  ["Does Holavisto guarantee visa approval?", "No. The relevant government authority makes the visa decision. We help prepare and support your application but cannot guarantee the outcome."],
  ["What happens if my visa is refused?", "A refusal is a decision made by the relevant authority. Our service fee relates to the preparation and support we provide, not to an approval outcome. Refund eligibility follows the applicable service terms and refund policy."],
  ["Is my information secure?", "Holavisto is built with private document handling, permission-controlled access and audit history. See our Privacy Policy for how information is collected, used and kept."],
];

// availability presentation
function availLabel(a) {
  if (a === "Available") return { text: "Service available", cls: "bg-tblue/10 text-tblue" };
  if (a === "Pilot") return { text: "Concierge available", cls: "bg-brandyellow/15 text-[#9a6b00]" };
  return { text: "Coming soon", cls: "bg-muted/10 text-muted" };
}

export default function Home() {
  const [corridors, setCorridors] = useState([]);
  const location = useLocation();

  useEffect(() => { api.get("/corridors").then((r) => setCorridors(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 320);
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-softcream">
      <Nav />

      {/* 2 · HERO */}
      <Section className="flex items-center !pb-12 !pt-8 sm:!py-12 lg:min-h-[calc(100vh-5rem)] lg:!py-0">
        <div className="grid w-full items-center gap-4 lg:grid-cols-2 lg:gap-16">
          {/* Image: first on mobile, right on desktop */}
          <motion.div className="relative order-1 lg:order-2" initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}>
            {/* mobile circular backdrop */}
            <div className="absolute bottom-2 left-1/2 h-[195px] w-[195px] -translate-x-1/2 rounded bg-cream sm:hidden" aria-hidden />
            {/* tablet / desktop blob */}
            <div className="absolute right-0 top-2 mx-auto hidden h-[520px] w-full rounded-md bg-cream sm:block lg:right-2 lg:h-[620px]" aria-hidden />
            <div className="relative mx-auto flex h-[215px] max-w-[230px] items-end justify-center sm:h-[520px] sm:max-w-none lg:h-[620px]">
              <img src={HERO_IMG} alt="Smiling traveller sitting on a suitcase holding a boarding pass" className=" h-full w-auto max-w-full object-contain drop-" loading="eager" />
            </div>
          </motion.div>
          {/* Text: second on mobile, left on desktop */}
          <motion.div className="order-2 text-center lg:order-1 lg:text-left" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-coral lg:hidden">Visa preparation, simplified</p>
            <div className="hidden lg:block"><Eyebrow>Visa preparation without the hassle</Eyebrow></div>
            {/* Short title on mobile */}
            <h1 className="font-serif text-[2rem] leading-[1.14] tracking-tight text-heading lg:hidden">
              Tell us where you're going.
            </h1>
            {/* Full title on desktop */}
            <h1 className="hidden font-serif tracking-tight text-heading lg:block lg:text-[4.7rem] lg:leading-[1.03]">
              Tell us where you're going. We'll take care of your visa.
            </h1>
            <p className="mx-auto mt-2.5 max-w-[19rem] text-[0.95rem] leading-relaxed text-muted lg:mx-0 lg:mt-7 lg:max-w-xl lg:text-lg">
              <span className="lg:hidden">Share your trip and documents. We'll prepare your visa, arrange human review and book your appointment.</span>
              <span className="hidden lg:inline">Share your trip, upload your passport and add your documents. We organize the requirements, prepare your application, check the important details, arrange human review and book your official visa appointment for you.</span>
            </p>
            <div className="mt-5 flex flex-col items-center gap-3 sm:flex-row lg:mt-8 lg:items-start">
              <YellowBtn to="/register" testid="hero-start-btn">Start my visa</YellowBtn>
              <div className="hidden sm:block">
                <GhostBtn to="/consultation" testid="hero-specialist-btn">Talk to a visa specialist</GhostBtn>
              </div>
            </div>
            {/* mobile trust row */}
            <div className="mt-6 flex items-center justify-center gap-x-4 gap-y-1.5 text-[11px] font-medium text-navy/70 lg:hidden">
              <span className="inline-flex items-center gap-1"><CheckCircle2 size={13} className="text-tblue" /> Human support</span>
              <span className="inline-flex items-center gap-1"><CheckCircle2 size={13} className="text-tblue" /> Secure &amp; clear</span>
            </div>
            <div className="mt-7 hidden flex-wrap gap-x-6 gap-y-2 text-sm font-medium text-navy lg:flex">
              {["Human support", "Secure document handling", "We book your appointment", "Clear pricing"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5"><CheckCircle2 size={15} className="text-tblue" /> {t}</span>
              ))}
            </div>
            <p className="mt-5 hidden max-w-xl text-xs leading-relaxed text-muted lg:block">
              Visa decisions are made by the relevant government authority. Holavisto provides visa preparation and support and does not guarantee an outcome.
            </p>
          </motion.div>
        </div>
      </Section>

      {/* 3 · DESTINATION FINDER */}
      <div className="bg-white">
        <Section id="destinations-finder">
          <Reveal className="max-w-2xl">
            <Eyebrow>Start with your destination</Eyebrow>
            <h2 className="font-serif text-3xl tracking-tight text-heading sm:text-4xl md:text-5xl">Where are you planning to go?</h2>
            <p className="mt-4 text-base leading-relaxed text-muted sm:text-lg">Choose your destination and tell us why you're travelling. We'll show you the visa service and preparation support available for your trip.</p>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {corridors.map((c, i) => {
              const a = availLabel(c.availability);
              const isAvail = c.availability === "Available";
              const isSoon = c.availability === "Coming soon";
              const cats = (c.visa_categories || []).map((v) => v.name);
              const purposeChips = [];
              if (cats.some((n) => /visitor|tourist|short/i.test(n))) purposeChips.push("Tourist");
              if (cats.some((n) => /student|study/i.test(n))) purposeChips.push("Student");
              if (cats.some((n) => /employ|work|skilled|business/i.test(n))) purposeChips.push("Work");
              return (
                <Reveal key={c.key} delay={i * 0.07}>
                  <div data-testid={`dest-card-${c.key}`} className={`group flex h-full flex-col overflow-hidden rounded-md border border-hborder bg-white transition-all duration-300   ${isSoon ? "opacity-90" : ""}`}>
                    <div className="relative h-40 overflow-hidden">
                      <img src={c.image_url} alt={c.destination} className="h-full w-full object-cover transition-transform duration-700 " loading="lazy" />
                      <span className={`absolute left-3 top-3 rounded px-3 py-1 text-xs font-semibold ${a.cls} bg-opacity-95`}>{a.text}</span>
                    </div>
                    <div className="flex flex-1 flex-col p-5">
                      <p className="flex items-center gap-2 text-lg font-semibold text-heading"><span aria-hidden>{c.flag}</span> {c.destination}</p>
                      {purposeChips.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {purposeChips.map((p) => <span key={p} className="rounded bg-cream px-2.5 py-0.5 text-xs font-medium text-navy">{p}</span>)}
                        </div>
                      )}
                      <p className="mt-3 flex-1 text-sm leading-relaxed text-muted">{c.summary?.split(".")[0]}.</p>
                      <div className="mt-5 flex flex-wrap gap-2">
                        {isAvail && (
                          <>
                            <Link to={`/destinations/${c.key}`} data-testid={`dest-view-${c.key}`} className="inline-flex items-center gap-1 rounded border border-hborder px-4 py-2 text-sm font-semibold text-navy transition-colors hover:border-navy">View service</Link>
                            <Link to="/register" data-testid={`dest-start-${c.key}`} className="inline-flex items-center gap-1 rounded bg-brandyellow px-4 py-2 text-sm font-semibold text-heading transition-transform ">Start preparation <ArrowRight size={14} /></Link>
                          </>
                        )}
                        {c.availability === "Pilot" && (
                          <Link to="/consultation" data-testid={`dest-concierge-${c.key}`} className="inline-flex items-center gap-1 rounded border border-hborder px-4 py-2 text-sm font-semibold text-navy transition-colors hover:border-navy"><MessageCircle size={14} /> Talk to concierge</Link>
                        )}
                        {isSoon && (
                          <Link to="/help" data-testid={`dest-notify-${c.key}`} className="inline-flex items-center gap-1 rounded border border-hborder px-4 py-2 text-sm font-semibold text-muted transition-colors hover:border-navy hover:text-navy">Notify me</Link>
                        )}
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
            {/* Other destinations */}
            <Reveal delay={corridors.length * 0.05}>
              <Link to="/consultation" data-testid="dest-card-other" className="group flex h-full min-h-[260px] flex-col items-start justify-center rounded-md border border-dashed border-hborder bg-cream/40 p-6 transition-colors hover:border-navy hover:bg-cream/70">
                <span className="grid h-12 w-12 place-items-center rounded-md bg-white"><Compass className="text-coral" size={22} /></span>
                <p className="mt-4 text-lg font-semibold text-heading">Another destination?</p>
                <p className="mt-2 text-sm leading-relaxed text-muted">We're expanding country by country. Tell our concierge team where you want to go and we'll confirm whether we can help.</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-tblue">Talk to our concierge <ArrowRight size={15} /></span>
              </Link>
            </Reveal>
          </div>
        </Section>
      </div>

      {/* 4 · PURPOSE OF TRAVEL */}
      <Section>
        <Reveal className="max-w-2xl">
          <Eyebrow>What is your trip for?</Eyebrow>
          <h2 className="font-serif text-3xl tracking-tight text-heading sm:text-4xl md:text-5xl">Choose the kind of visa support you need.</h2>
          <p className="mt-4 text-base leading-relaxed text-muted sm:text-lg">You don't need to understand visa categories. Tell us why you're travelling and we'll guide you to the right service.</p>
        </Reveal>
        <RevealGroup className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {purposes.map((p) => (
            <Item key={p.title} className="group rounded-md border border-hborder bg-white p-6 transition-all duration-300 ">
              <span className="mb-4 inline-grid h-12 w-12 place-items-center rounded-md bg-ltblue transition-transform duration-300 "><p.Icon className="text-tblue" size={22} /></span>
              <h3 className="text-lg font-semibold text-heading">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted">{p.body}</p>
            </Item>
          ))}
        </RevealGroup>
        <Reveal className="mt-10"><YellowBtn to="/register" testid="purpose-cta">Find my visa service</YellowBtn></Reveal>
      </Section>

      {/* 5 · CORE SERVICE PROMISE + 9 · HOW IT WORKS (combined journey) */}
      <div className="bg-white">
        <Section id="how">
          <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
            <Reveal className="lg:sticky lg:top-28">
              <Eyebrow>We handle the preparation</Eyebrow>
              <h2 className="font-serif text-3xl tracking-tight text-heading sm:text-4xl md:text-5xl">Give us the details. We'll take care of the paperwork.</h2>
              <p className="mt-4 text-base leading-relaxed text-muted sm:text-lg">No more digging through pages of requirements or wondering whether everything matches. From your passport to a prepared application, we bring the work into one clear process.</p>
              <div className="mt-7 overflow-hidden rounded-md border border-hborder ">
                <img src={SERVICE_IMG} alt="Traveller organizing passport and documents to prepare a visa application" className="h-56 w-full object-cover sm:h-72" loading="lazy" />
              </div>
              <div className="mt-6"><YellowBtn to="/how-it-works" testid="service-how-cta">See how it works</YellowBtn></div>
            </Reveal>
            <RevealGroup className="space-y-4">
              {journey.map(([n, t, b]) => (
                <Item key={n} className="group flex gap-5 rounded-md border border-hborder bg-softcream p-5 transition- duration-300 ">
                  <span className="font-serif text-2xl font-bold text-brandyellow">{n}</span>
                  <div>
                    <h3 className="text-lg font-semibold text-heading">{t}</h3>
                    <p className="mt-1 text-[15px] leading-relaxed text-muted">{b}</p>
                  </div>
                </Item>
              ))}
            </RevealGroup>
          </div>
        </Section>
      </div>

      {/* 6 · WHAT WE HANDLE */}
      <Section>
        <Reveal className="max-w-2xl">
          <Eyebrow>What Holavisto takes care of</Eyebrow>
          <h2 className="font-serif text-3xl tracking-tight text-heading sm:text-4xl md:text-5xl">The difficult parts are handled for you.</h2>
        </Reveal>
        <RevealGroup className="mt-10 grid gap-6 sm:grid-cols-2">
          {handles.map((h) => (
            <Item key={h.title} className="group flex gap-5 rounded-md border border-hborder bg-white p-6 transition-all duration-300 sm:p-8">
              <span className="inline-grid h-12 w-12 shrink-0 place-items-center rounded-md bg-cream transition-transform duration-300 group-hover:rotate-3"><h.Icon className="text-coral" size={22} /></span>
              <div>
                <h3 className="text-xl font-semibold text-heading">{h.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-muted">{h.body}</p>
              </div>
            </Item>
          ))}
        </RevealGroup>
      </Section>

      {/* 7 · THE PROBLEM */}
      <Section className="!pt-0">
        <Reveal className="rounded-md bg-navy px-6 py-12 text-white sm:py-14 md:px-14">
          <div className="max-w-3xl">
            <Eyebrow light>Why visa applications feel so hard</Eyebrow>
            <h2 className="font-serif text-3xl tracking-tight sm:text-4xl md:text-5xl">The journey is exciting. The paperwork can be exhausting.</h2>
            <p className="mt-4 text-[15px] leading-relaxed text-white/70">Visa preparation often means hunting for information, collecting documents, entering the same details more than once and checking everything lines up.</p>
          </div>
          <RevealGroup className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {problems.map(([t, b], i) => (
              <Item key={t} className="group rounded-md border border-white/10 bg-white/5 p-6 transition-colors hover:bg-white/10">
                <span className="grid h-11 w-11 place-items-center rounded-md bg-brandyellow/15 font-serif text-lg font-semibold text-brandyellow transition-transform duration-300 ">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-4 text-lg font-semibold">{t}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-white/70">{b}</p>
              </Item>
            ))}
          </RevealGroup>
          <p className="mt-8 font-serif text-xl text-brandyellow">Holavisto brings the preparation into one guided experience.</p>
        </Reveal>
      </Section>

      {/* 8 · HOLAVISTO VS DOING IT ALONE */}
      <div className="bg-white">
        <Section>
          <Reveal className="max-w-2xl">
            <Eyebrow>Why people use Holavisto</Eyebrow>
            <h2 className="font-serif text-3xl tracking-tight text-heading sm:text-4xl md:text-5xl">Spend less time figuring out the process.</h2>
            <p className="mt-4 text-base leading-relaxed text-muted sm:text-lg">You can prepare a visa application yourself. Holavisto is for people who would rather have it organized and supported by a dedicated service.</p>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            <Reveal className="rounded-md border-2 border-tblue/30 bg-ltblue/30 p-7">
              <p className="text-sm font-bold uppercase tracking-[0.16em] text-tblue">With Holavisto</p>
              <ul className="mt-5 space-y-3">
                {withUs.map((x) => <li key={x} className="flex items-start gap-2.5 text-[15px] text-navy"><Check size={18} className="mt-0.5 shrink-0 text-tblue" /> {x}</li>)}
              </ul>
            </Reveal>
            <Reveal delay={0.1} className="rounded-md border border-hborder bg-softcream p-7">
              <p className="text-sm font-bold uppercase tracking-[0.16em] text-muted">Doing it yourself</p>
              <ul className="mt-5 space-y-3">
                {onYourOwn.map((x) => <li key={x} className="flex items-start gap-2.5 text-[15px] text-muted"><X size={18} className="mt-0.5 shrink-0 text-coral/70" /> {x}</li>)}
              </ul>
            </Reveal>
          </div>
          <p className="mt-8 text-center text-[15px] text-muted">Holavisto does not replace the official process. We make the preparation easier to manage.</p>
        </Section>
      </div>

      {/* 10 · SERVICE PACKAGES */}
      <Section id="packages">
        <Reveal className="max-w-2xl">
          <Eyebrow>Choose your level of support</Eyebrow>
          <h2 className="font-serif text-3xl tracking-tight text-heading sm:text-4xl md:text-5xl">How much of the work would you like us to handle?</h2>
          <p className="mt-4 text-base leading-relaxed text-muted sm:text-lg">Pick the service that matches how much support you want. Each price is the Holavisto service fee; government and provider charges are shown separately before you begin.</p>
        </Reveal>
        <RevealGroup className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {packages.map((p) => (
            <Item key={p.name} data-testid={`package-${p.name.toLowerCase().split(" ")[0]}`}
              className={`relative flex h-full flex-col rounded-md border p-6 transition-all duration-300   ${p.highlight ? "border-navy bg-navy text-white" : "border-hborder bg-white"}`}>
              {p.highlight && <span className="absolute -top-3 left-6 rounded bg-brandyellow px-3 py-1 text-xs font-bold text-heading">Full service</span>}
              <p className={`text-xs font-bold uppercase tracking-[0.16em] ${p.highlight ? "text-brandyellow" : "text-coral"}`}>{p.tag}</p>
              <h3 className={`mt-2 font-serif text-2xl ${p.highlight ? "text-white" : "text-heading"}`}>{p.name}</h3>
              <p className={`mt-2 flex items-baseline gap-1 ${p.highlight ? "text-white" : "text-heading"}`} data-testid={`package-price-${p.name.toLowerCase().split(" ")[0]}`}>
                <span className="font-serif text-3xl font-bold">&euro;{p.price}</span>
                <span className={`text-xs font-medium ${p.highlight ? "text-white/70" : "text-muted"}`}>service fee</span>
              </p>
              <p className={`mt-2 text-sm leading-relaxed ${p.highlight ? "text-white/75" : "text-muted"}`}>{p.body}</p>
              <ul className="mt-5 flex-1 space-y-2.5">
                {p.feats.map((f) => (
                  <li key={f} className={`flex items-start gap-2 text-sm ${p.highlight ? "text-white/90" : "text-navy"}`}>
                    <CheckCircle2 size={15} className={`mt-0.5 shrink-0 ${p.highlight ? "text-brandyellow" : "text-tblue"}`} /> {f}
                  </li>
                ))}
              </ul>
              <Link to={p.to} data-testid={`package-cta-${p.name.toLowerCase().split(" ")[0]}`}
                className={`mt-6 inline-flex items-center justify-center gap-2 rounded px-5 py-3 text-sm font-semibold transition-all ${p.highlight ? "bg-brandyellow text-heading " : "border border-hborder text-navy hover:border-navy"}`}>
                {p.cta} <ChevronRight size={15} />
              </Link>
            </Item>
          ))}
        </RevealGroup>
        <p className="mt-6 text-sm text-muted">Professional immigration services are provided only by people who are legally permitted and appropriately authorized for the relevant service and jurisdiction.</p>
      </Section>

      {/* 11 · PRICING TRANSPARENCY */}
      <div className="bg-white">
        <Section id="pricing">
          <Reveal className="max-w-2xl">
            <Eyebrow>Clear pricing</Eyebrow>
            <h2 className="font-serif text-3xl tracking-tight text-heading sm:text-4xl md:text-5xl">Know what the service costs before you begin.</h2>
            <p className="mt-4 text-base leading-relaxed text-muted sm:text-lg">We separate the Holavisto service fee from government charges and other third-party costs, so you always understand exactly what you're paying for.</p>
          </Reveal>

          {/* Per-destination Holavisto service fee */}
          <div className="mt-10">
            <p className="text-sm font-bold uppercase tracking-[0.16em] text-coral">Holavisto service fee by destination</p>
            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {corridors.map((c, i) => (
                <Reveal key={c.key} delay={i * 0.06}>
                  <div data-testid={`price-card-${c.key}`} className="flex items-center justify-between rounded-md border border-hborder bg-softcream p-5">
                    <span className="flex items-center gap-2.5 text-[15px] font-semibold text-heading">
                      <span aria-hidden className="text-xl">{c.flag}</span> {c.destination}
                    </span>
                    <span className="flex items-baseline gap-1">
                      <span className="text-xs font-medium text-muted">from</span>
                      <span className="font-serif text-2xl font-bold text-navy" data-testid={`price-amount-${c.key}`}>&euro;{corridorPrices[c.key] ?? 70}</span>
                    </span>
                  </div>
                </Reveal>
              ))}
            </div>
            <p className="mt-4 text-sm text-muted">Service fee shown is the Holavisto preparation and support fee per applicant. Government visa fees, visa-centre or provider fees and other official costs are charged separately and shown clearly before you pay.</p>
          </div>

          <p className="mt-10 text-sm font-bold uppercase tracking-[0.16em] text-coral">What makes up your total</p>
          <RevealGroup className="mt-5 grid gap-4 sm:grid-cols-2">
            {feeLines.map(([t, b], i) => (
              <Item key={t} className="flex items-start gap-4 rounded-md border border-hborder bg-softcream p-5">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-white font-serif text-sm font-bold text-tblue">{i + 1}</span>
                <div>
                  <p className="font-semibold text-heading">{t}</p>
                  <p className="mt-1 text-sm leading-relaxed text-muted">{b}</p>
                </div>
              </Item>
            ))}
          </RevealGroup>
          <p className="mt-6 text-sm text-muted">Government and third-party charges are never presented as Holavisto revenue.</p>
        </Section>
      </div>

      {/* 12 · HUMAN REVIEW */}
      <Section>
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <Eyebrow>Real people behind the service</Eyebrow>
            <h2 className="font-serif text-3xl tracking-tight text-heading sm:text-4xl md:text-5xl">Technology moves fast. People make it feel safe.</h2>
            <p className="mt-4 text-base leading-relaxed text-muted sm:text-lg">Our technology organizes your information and documents. Before you move forward, a Holavisto specialist reviews your preparation and points out anything that still needs your attention.</p>
            <p className="mt-4 text-[15px] font-medium text-navy">You see the preparation before continuing. You stay in control of your information.</p>
            <div className="mt-6"><GhostBtn to="/how-it-works" testid="review-cta" icon={UserCheck}>Learn about human review</GhostBtn></div>
          </Reveal>
          <Reveal delay={0.12} className="rounded-md border border-hborder bg-white p-6 sm:p-8">
            <p className="text-sm font-semibold text-heading">Preparation review</p>
            <div className="mt-5 space-y-3">
              {reviewRows.map(([label, status, kind]) => (
                <div key={label} className="flex items-center justify-between rounded-md border border-hborder bg-softcream px-4 py-3">
                  <span className="text-sm font-medium text-navy">{label}</span>
                  <span className={`inline-flex items-center gap-1.5 rounded px-3 py-1 text-xs font-semibold ${kind === "ok" ? "bg-tblue/10 text-tblue" : kind === "warn" ? "bg-coral/10 text-coral" : "bg-brandyellow/15 text-[#9a6b00]"}`}>
                    {kind === "ok" ? <Check size={13} /> : kind === "warn" ? <span className="h-1.5 w-1.5 rounded bg-coral" /> : <ChevronRight size={13} />} {status}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-muted">Illustrative preview. Your actual review reflects your own case.</p>
          </Reveal>
        </div>
      </Section>

      {/* 13 · APPOINTMENT SUPPORT */}
      <div className="bg-white">
        <Section>
          <Reveal className="max-w-2xl">
            <Eyebrow>Visa appointment booking</Eyebrow>
            <h2 className="font-serif text-3xl tracking-tight text-heading sm:text-4xl md:text-5xl">We book your visa appointment for you.</h2>
            <p className="mt-4 text-base leading-relaxed text-muted sm:text-lg">Booking a visa appointment is one of the hardest parts of the journey. Where the official process allows, our team books it for you, tracks availability and keeps you updated. Where you must attend in person, we make sure you are fully prepared.</p>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            <Reveal className="rounded-md border border-hborder bg-softcream p-7">
              <p className="text-sm font-bold uppercase tracking-[0.16em] text-tblue">We take care of</p>
              <ul className="mt-5 space-y-3">{appointmentHelp.map((x) => <li key={x} className="flex items-start gap-2.5 text-[15px] text-navy"><Check size={18} className="mt-0.5 shrink-0 text-tblue" /> {x}</li>)}</ul>
            </Reveal>
            <Reveal delay={0.1} className="rounded-md border border-hborder bg-softcream p-7">
              <p className="text-sm font-bold uppercase tracking-[0.16em] text-coral">You may still need to</p>
              <ul className="mt-5 space-y-3">{appointmentYou.map((x) => <li key={x} className="flex items-start gap-2.5 text-[15px] text-muted"><CalendarClock size={18} className="mt-0.5 shrink-0 text-coral/70" /> {x}</li>)}</ul>
            </Reveal>
          </div>
          <p className="mt-6 text-sm text-muted">Appointment availability and timing depend on the relevant authority or official provider. We cannot guarantee a particular appointment date.</p>
        </Section>
      </div>

      {/* 14 · FAMILY & GROUP */}
      <Section>
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal className="group relative order-2 overflow-hidden rounded-md border border-hborder lg:order-1">
            <img src={FAMILY_IMG} alt="Family travelling together with luggage" className="h-72 w-full object-cover transition-transform duration-700 sm:h-96" loading="lazy" />
          </Reveal>
          <Reveal delay={0.1} className="order-1 lg:order-2">
            <Eyebrow>Travelling together</Eyebrow>
            <h2 className="font-serif text-3xl tracking-tight text-heading sm:text-4xl md:text-5xl">Keep the whole trip organized in one place.</h2>
            <p className="mt-4 text-base leading-relaxed text-muted sm:text-lg">Create one group journey while keeping each traveller's personal documents and information separate.</p>
            <div className="mt-6 space-y-2.5">
              {[["Applicant 1", "Complete", "ok"], ["Applicant 2", "Complete", "ok"], ["Applicant 3", "Action needed", "warn"], ["Applicant 4", "Under review", "info"]].map(([n, s, k]) => (
                <div key={n} className="flex items-center justify-between rounded-md border border-hborder bg-white px-4 py-2.5">
                  <span className="text-sm font-medium text-navy">{n}</span>
                  <span className={`rounded px-3 py-1 text-xs font-semibold ${k === "ok" ? "bg-tblue/10 text-tblue" : k === "warn" ? "bg-coral/10 text-coral" : "bg-brandyellow/15 text-[#9a6b00]"}`}>{s}</span>
                </div>
              ))}
            </div>
            <div className="mt-6"><YellowBtn to="/register" testid="family-cta">Start a group application</YellowBtn></div>
          </Reveal>
        </div>
      </Section>

      {/* 15 · WHATSAPP + 16 · CONCIERGE */}
      <Section className="!pt-0">
        <div className="grid gap-6 lg:grid-cols-2">
          <Reveal className="flex flex-col justify-between rounded-md bg-cream p-8 sm:p-10">
            <div>
              <Eyebrow>Need help?</Eyebrow>
              <h2 className="font-serif text-2xl tracking-tight text-heading sm:text-3xl">Talk to a real person.</h2>
              <p className="mt-3 text-[15px] leading-relaxed text-muted">Not sure which visa service you need, or have a question about documents, pricing or the process? Send us a message and our team will help you find where to start.</p>
            </div>
            <div className="mt-6"><GhostBtn to="/consultation" testid="whatsapp-cta">Message a visa specialist</GhostBtn></div>
          </Reveal>
          <Reveal delay={0.1} className="flex flex-col justify-between rounded-md bg-navy p-8 text-white sm:p-10">
            <div>
              <Eyebrow light>Your destination isn't listed?</Eyebrow>
              <h2 className="font-serif text-2xl tracking-tight sm:text-3xl">Tell us where you want to go.</h2>
              <p className="mt-3 text-[15px] leading-relaxed text-white/75">We're expanding our services country by country. If your destination isn't available yet, tell our team about your trip and we'll let you know whether we can help.</p>
            </div>
            <div className="mt-6">
              <Link to="/consultation" data-testid="concierge-cta" className="inline-flex items-center justify-center gap-2 rounded bg-brandyellow px-7 py-3.5 text-sm font-semibold text-heading transition-all ">Talk to our concierge <ArrowRight size={17} /></Link>
              <p className="mt-3 text-xs text-white/60">Our team confirms availability, scope and pricing before you begin.</p>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* 17 · VISA VAULT */}
      <div className="bg-white">
        <Section>
          <Reveal className="max-w-2xl">
            <Eyebrow>Ready for your next journey</Eyebrow>
            <h2 className="font-serif text-3xl tracking-tight text-heading sm:text-4xl md:text-5xl">Keep your visa documents organized for the future.</h2>
            <p className="mt-4 text-base leading-relaxed text-muted sm:text-lg">Your Holavisto account keeps your key information and documents organized, so future applications can start from a stronger foundation.</p>
          </Reveal>
          <RevealGroup className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {vaultCards.map((v) => (
              <Item key={v} className="flex items-center gap-3 rounded-md border border-hborder bg-softcream p-5">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-white"><Lock className="text-tblue" size={18} /></span>
                <span className="font-medium text-navy">{v}</span>
              </Item>
            ))}
          </RevealGroup>
          <p className="mt-6 text-sm text-muted">Information and documents must be reviewed and updated for every new application. Previous documents are not automatically treated as valid for a future application.</p>
        </Section>
      </div>

      {/* 18 · FOR APPLICANTS + 19 · FOR AGENCIES */}
      <Section>
        <RevealGroup className="grid gap-6 lg:grid-cols-2">
          <Item className="rounded-md border border-hborder bg-softcream p-6 transition- duration-300 sm:p-8">
            <Eyebrow>For individual travellers</Eyebrow>
            <h3 className="font-serif text-2xl text-heading sm:text-3xl">Your visa preparation team, in one place.</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-muted">From your first question to your final review, Holavisto keeps your application organized and shows you exactly what needs attention.</p>
            <ul className="mt-5 grid grid-cols-1 gap-2.5 text-sm text-navy sm:grid-cols-2">
              {applicantFeats.map((x) => <li key={x} className="flex items-center gap-2"><CheckCircle2 size={16} className="text-tblue" /> {x}</li>)}
            </ul>
            <Link to="/applicants" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-tblue hover:underline" data-testid="home-applicants-link">Prepare my visa <ArrowRight size={15} /></Link>
          </Item>
          <Item className="rounded-md border border-hborder bg-navy p-6 text-white transition- duration-300 sm:p-8">
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-brandyellow">For visa agencies</p>
            <h3 className="font-serif text-2xl sm:text-3xl">Give your team one place to manage every case.</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-white/75">Manage clients, documents, preparation, findings, assignments, reviews and deadlines from one professional workspace.</p>
            <ul className="mt-5 grid grid-cols-1 gap-2.5 text-sm text-white/90 sm:grid-cols-2">
              {agencyFeats.map((x) => <li key={x} className="flex items-center gap-2"><CheckCircle2 size={16} className="text-brandyellow" /> {x}</li>)}
            </ul>
            <Link to="/agencies" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brandyellow hover:underline" data-testid="home-agencies-link">Explore Holavisto for agencies <ArrowRight size={15} /></Link>
          </Item>
        </RevealGroup>
      </Section>

      {/* 20 · TRUST & SECURITY */}
      <div className="bg-white">
        <Section>
          <Reveal className="max-w-2xl">
            <Eyebrow>Built to handle important documents</Eyebrow>
            <h2 className="font-serif text-3xl tracking-tight text-heading sm:text-4xl md:text-5xl">Your visa documents deserve serious care.</h2>
          </Reveal>
          <RevealGroup className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {trust.map((t) => (
              <Item key={t.title} className="group rounded-md border border-hborder bg-softcream p-6 transition-all duration-300 ">
                <span className="mb-4 inline-grid h-11 w-11 place-items-center rounded-md bg-ltblue transition-transform duration-300 "><t.Icon className="text-tblue" size={20} /></span>
                <h3 className="text-lg font-semibold text-heading">{t.title}</h3>
                <p className="mt-1.5 text-[15px] leading-relaxed text-muted">{t.body}</p>
              </Item>
            ))}
          </RevealGroup>
          <p className="mt-6 text-sm text-muted">We do not claim certifications, security standards or partnerships unless they have been verified and implemented.</p>
        </Section>
      </div>

      {/* 21 · WHY HOLAVISTO */}
      <Section>
        <Reveal className="max-w-2xl">
          <Eyebrow>Why Holavisto</Eyebrow>
          <h2 className="font-serif text-3xl tracking-tight text-heading sm:text-4xl md:text-5xl">A simpler way to prepare for a complicated process.</h2>
        </Reveal>
        <RevealGroup className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {why.map((w) => (
            <Item key={w.title} className="group rounded-md border border-hborder bg-white p-6 transition-all duration-300 ">
              <span className="mb-4 inline-grid h-11 w-11 place-items-center rounded-md bg-cream transition-transform duration-300 "><w.Icon className="text-coral" size={20} /></span>
              <h3 className="text-lg font-semibold text-heading">{w.title}</h3>
              <p className="mt-1.5 text-[15px] leading-relaxed text-muted">{w.body}</p>
            </Item>
          ))}
        </RevealGroup>
      </Section>

      {/* 22 · STUDENT + 23 · TOURIST */}
      <div className="bg-white">
        <Section>
          <div className="grid gap-8 lg:grid-cols-2">
            <Reveal className="group overflow-hidden rounded-md border border-hborder bg-softcream">
              <div className="h-52 overflow-hidden"><img src={STUDENT_IMG} alt="Students walking across a university campus abroad" className="h-full w-full object-cover transition-transform duration-700 " loading="lazy" /></div>
              <div className="p-7">
                <Eyebrow>Study abroad</Eyebrow>
                <h3 className="font-serif text-2xl text-heading sm:text-3xl">Preparing to study abroad should start with a clear plan.</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-muted">We help students organize the information, documents and preparation their visa process needs. Student services show as available only where a real workflow exists.</p>
                <Link to="/destinations" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-tblue hover:underline" data-testid="student-cta">Explore student visa services <ArrowRight size={15} /></Link>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="group overflow-hidden rounded-md border border-hborder bg-softcream">
              <div className="h-52 overflow-hidden"><img src={PLAN_IMG} alt="Traveller planning a trip with a world map and passport" className="h-full w-full object-cover transition-transform duration-700 " loading="lazy" /></div>
              <div className="p-7">
                <Eyebrow>Travel the world</Eyebrow>
                <h3 className="font-serif text-2xl text-heading sm:text-3xl">You plan the destination. We'll help with the paperwork.</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-muted">Tell us where you're travelling, upload your passport and let Holavisto organize the preparation around your trip.</p>
                <Link to="/destinations" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-tblue hover:underline" data-testid="tourist-cta">Explore tourist visa services <ArrowRight size={15} /></Link>
              </div>
            </Reveal>
          </div>
        </Section>
      </div>

      {/* 24 · FAQ */}
      <Section id="faq">
        <Reveal className="max-w-2xl">
          <Eyebrow>Questions before you start</Eyebrow>
          <h2 className="font-serif text-3xl tracking-tight text-heading sm:text-4xl md:text-5xl">Everything to know before your application begins.</h2>
        </Reveal>
        <Reveal className="mt-8 rounded-md border border-hborder bg-white px-6 sm:px-8" data-testid="faq-accordion">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map(([q, a], i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="py-5 text-left text-base font-semibold text-heading hover:no-underline">{q}</AccordionTrigger>
                <AccordionContent className="text-[15px] leading-relaxed text-muted">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </Section>

      {/* 25 · FINAL CTA */}
      <Section className="!pt-0">
        <Reveal className="rounded-md bg-cream px-6 py-14 text-center sm:py-16 md:px-14">
          <h2 className="mx-auto max-w-2xl font-serif text-3xl tracking-tight text-heading sm:text-4xl md:text-5xl">Ready to leave the visa paperwork to us?</h2>
          <p className="mx-auto mt-4 max-w-xl text-muted">Tell us where you're going, tell us what you're travelling for and upload your passport. We'll guide you through the preparation from there.</p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <YellowBtn to="/register" testid="final-cta-btn">Start my visa</YellowBtn>
            <GhostBtn to="/consultation" testid="final-specialist-btn">Talk to a visa specialist</GhostBtn>
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm font-medium text-navy">
            {["Human supported", "Secure document handling", "Clear service pricing", "Official process guidance"].map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5"><CheckCircle2 size={15} className="text-tblue" /> {t}</span>
            ))}
          </div>
          <p className="mx-auto mt-6 max-w-xl text-xs leading-relaxed text-muted">Holavisto prepares and supports visa applications. The relevant government authority makes the final decision.</p>
        </Reveal>
      </Section>

      <Footer />
    </div>
  );
}
